import {
    ReactElement,
    useRef,
    useState,
    Dispatch,
    FormEventHandler,
    forwardRef,
    SetStateAction,
    useCallback,
    useImperativeHandle
} from 'react';
import { GeneralTeamSettingsSectionFormRef } from './general-team-settings-section';
import { Procedure, Team } from '@/api';
import { useUpdateTeamAction } from '../actions';
import {
    UISection,
    UISectionContent,
    UISectionFooter,
    UISectionHeader,
    UIText
} from '@/ui';
import { uuidv4 } from '@/common/util';
import {
    ProcedureData,
    EditableProceduresList,
    ProceduresList
} from '@/common/components';
import { useCompaniesTranslation } from '../hooks';

type TeamProceduresSettingsSectionProps = { team: Team };

export function TeamProceduresSettingsSection(
    props: TeamProceduresSettingsSectionProps
): ReactElement {
    const { team } = props;

    const formRef = useRef<GeneralTeamSettingsSectionFormRef>(null);
    const updateTeam = useUpdateTeamAction({ teamId: team.id });

    const [isEditing, setIsEditing] = useState(false);

    function onSubmit(data: { procedures: Procedure[] }) {
        const { procedures } = data;
        updateTeam
            .updateTeam({
                procedures
            })
            .then(() => {
                setIsEditing(false);
            })
            .catch(() => {}); // Error handled in the action
    }

    return (
        <TeamProceduresSettingsSectionForm
            canEdit={updateTeam.isAllowed}
            ref={formRef}
            initialProcedures={team.procedures}
            loading={updateTeam.loading}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            onSubmit={onSubmit}
        />
    );
}

type TeamProceduresSettingsSectionFormProps = {
    canEdit: boolean;
    initialProcedures: Procedure[];
    loading: boolean;
    isEditing: boolean;
    setIsEditing: Dispatch<SetStateAction<boolean>>;
    onSubmit: (data: { procedures: Procedure[] }) => void;
};

type TeamProceduresSettingsSectionFormRef = {
    resetForm: () => void;
};

const TeamProceduresSettingsSectionForm = forwardRef<
    TeamProceduresSettingsSectionFormRef,
    TeamProceduresSettingsSectionFormProps
>((props, ref): ReactElement => {
    const {
        canEdit,
        initialProcedures,
        loading,
        isEditing,
        setIsEditing,
        onSubmit
    } = props;

    const { t } = useCompaniesTranslation();

    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);

    const [procedures, setProcedures] = useState<ProcedureData[]>(
        initialProcedures.map((procedure) => ({
            id: uuidv4(),
            isValid: false,
            ...procedure
        }))
    );

    /**
     * Procedures
     */
    const checkProcedures = useCallback(
        (procedures: ProcedureData[]): string => {
            const invalidProcedures = procedures.filter(
                (procedure) => !procedure.isValid
            );
            if (invalidProcedures.length > 0) {
                return t(
                    'components.team_procedures_settings_section.error_incomplete'
                );
            }

            return '';
        },
        [t]
    );

    function submitForm() {
        onSubmit?.({
            procedures: procedures.map((p) => ({
                text: p.text
            }))
        });
    }

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();

        setShowRealTimeErrors(true);

        if (!checkProcedures(procedures)) {
            setShowRealTimeErrors(false);

            submitForm();
        }
    };

    function resetForm() {
        setProcedures(
            initialProcedures.map((procedure) => ({
                id: uuidv4(),
                isValid: false,
                ...procedure
            }))
        );
        setShowRealTimeErrors(false);
        setIsEditing(false);
    }

    useImperativeHandle(ref, () => ({
        resetForm() {
            resetForm();
        }
    }));

    const sectionTitle = t('components.team_procedures_settings_section.title');

    return (
        <UISection>
            {isEditing ? (
                <EditableForm
                    sectionTitle={sectionTitle}
                    procedures={procedures}
                    setProcedures={setProcedures}
                    loading={loading}
                    showRealTimeErrors={showRealTimeErrors}
                    canEdit={canEdit}
                    resetForm={resetForm}
                    handleSubmit={handleSubmit}
                />
            ) : (
                <NonEditableForm
                    sectionTitle={sectionTitle}
                    procedures={procedures}
                    onEditClick={() => {
                        setIsEditing(true);
                        setShowRealTimeErrors(false);
                    }}
                    canEdit={canEdit}
                />
            )}
        </UISection>
    );
});

function NonEditableForm(props: {
    sectionTitle: string;
    procedures: ProcedureData[];
    onEditClick: () => void;
    canEdit: boolean;
}): ReactElement {
    const { sectionTitle, procedures, onEditClick, canEdit } = props;

    const { t } = useCompaniesTranslation();
    return (
        <>
            <UISectionHeader
                title={sectionTitle}
                rightButtons={
                    canEdit
                        ? [
                              {
                                  text: t('shared.labels.edit'),
                                  variant: 'textDefault',
                                  type: 'button',
                                  onClick: onEditClick
                              }
                          ]
                        : []
                }
            />
            <UISectionContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 24
                    }}
                >
                    <ProceduresDescription />

                    <div>
                        <ProceduresList procedures={procedures} />
                    </div>
                </div>
            </UISectionContent>
        </>
    );
}

function EditableForm(props: {
    sectionTitle: string;
    procedures: ProcedureData[];
    setProcedures: Dispatch<SetStateAction<ProcedureData[]>>;
    showRealTimeErrors: boolean;
    canEdit: boolean;
    loading: boolean;
    handleSubmit: FormEventHandler;
    resetForm: () => void;
}): ReactElement {
    const {
        sectionTitle,
        procedures,
        setProcedures,
        showRealTimeErrors,
        canEdit,
        loading,
        handleSubmit,
        resetForm
    } = props;

    const { t } = useCompaniesTranslation();

    return (
        <form onSubmit={handleSubmit} noValidate>
            <UISectionHeader title={sectionTitle} />
            <UISectionContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 24
                    }}
                >
                    <ProceduresDescription />

                    <div>
                        <EditableProceduresList
                            procedures={procedures}
                            setProcedures={setProcedures}
                            showRealTimeErrors={showRealTimeErrors}
                            canEdit={canEdit}
                        />
                    </div>
                </div>
            </UISectionContent>

            <UISectionFooter
                actions={[
                    {
                        variant: 'primary',
                        type: 'submit',
                        loading: loading,
                        disabled: loading,
                        text: loading
                            ? t('shared.labels.saving')
                            : t('shared.labels.save')
                    },
                    {
                        text: t('shared.labels.cancel'),
                        disabled: loading,
                        onClick: resetForm
                    }
                ]}
            />
        </form>
    );
}

function ProceduresDescription(): ReactElement {
    const { t } = useCompaniesTranslation();
    return (
        <UIText type="default2">
            {t('components.team_procedures_settings_section.explainer')}
        </UIText>
    );
}
